import React, { useState, useMemo } from 'react';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { ChevronsUpDown, Check, Search } from 'lucide-react';

function CustomerCombobox({
  customers = [],
  items = [],
  value,
  selectedId,
  onSelect,
  placeholder = "Select Customer",
  testId,
  disabled = false,
  className = ""
}) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');

  const list = useMemo(() => (customers && customers.length > 0) ? customers : (items || []), [customers, items]);
  const activeId = value !== undefined && value !== null && value !== '' ? value : (selectedId || '');
  const selected = list.find(c => String(c.id) === String(activeId));

  const getLabel = (c) => {
    if (!c) return '';
    return c.business_name || c.name || c.company_name || c.contact_person || 'Unknown';
  };

  const filteredCustomers = useMemo(() => {
    if (!search) return list;
    const s = search.toLowerCase();
    return list.filter(c => {
      const name = getLabel(c).toLowerCase();
      const contact = (c.contact_person || '').toLowerCase();
      const city = (c.city || c.head_office_location || '').toLowerCase();
      return name.includes(s) || contact.includes(s) || city.includes(s);
    });
  }, [list, search]);

  const handleSelectCustomer = (c) => {
    if (onSelect) {
      onSelect(c.id, c);
    }
    setOpen(false);
    setSearch('');
  };

  return (
    <Popover open={open} onOpenChange={(isOpen) => { if (!disabled) { setOpen(isOpen); if (!isOpen) setSearch(''); } }}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          role="combobox"
          aria-expanded={open}
          disabled={disabled}
          data-testid={testId}
          className={`w-full justify-between border-gray-200 font-ibm font-normal text-xs h-9 hover:bg-gray-50 bg-white ${disabled ? 'opacity-60 cursor-not-allowed bg-gray-50' : ''} ${className}`}
        >
          <span className="truncate text-left text-slate-800">
            {selected ? getLabel(selected) : <span className="text-gray-400">{placeholder}</span>}
          </span>
          <ChevronsUpDown className="ml-2 h-3.5 w-3.5 shrink-0 opacity-50 text-gray-500" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[--radix-popover-trigger-width] min-w-[300px] p-0 shadow-lg border border-gray-200 bg-white z-50" align="start">
        <div className="flex items-center border-b border-gray-100 px-3 py-2 bg-gray-50/50">
          <Search className="mr-2 h-3.5 w-3.5 shrink-0 opacity-50 text-gray-500" />
          <Input 
            placeholder="Search customer..." 
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="border-0 h-7 text-xs focus-visible:ring-0 focus-visible:ring-offset-0 px-0 bg-transparent font-ibm"
            autoFocus
          />
        </div>
        <div className="max-h-[260px] overflow-y-auto p-1 divide-y divide-gray-50">
          {filteredCustomers.length === 0 ? (
            <div className="py-6 text-center text-xs text-muted-foreground font-ibm">No customer found.</div>
          ) : (
            filteredCustomers.map(c => {
              const displayName = getLabel(c);
              const isSelected = String(activeId) === String(c.id);
              const locationText = [c.city, c.state].filter(Boolean).join(', ') || c.head_office_location || '';
              return (
                <div
                  key={c.id}
                  onClick={() => handleSelectCustomer(c)}
                  className={`relative flex cursor-pointer select-none items-center rounded-md px-2.5 py-2 text-xs outline-none transition-colors ${
                    isSelected ? 'bg-blue-50 text-accent-blue font-semibold' : 'hover:bg-gray-100 text-slate-700'
                  }`}
                  data-testid={`customer-option-${c.id}`}
                >
                  <Check className={`mr-2 h-3.5 w-3.5 shrink-0 ${isSelected ? "opacity-100 text-accent-blue" : "opacity-0"}`} />
                  <div className="flex flex-col min-w-0">
                    <span className="truncate font-medium text-slate-800">{displayName}</span>
                    {locationText && (
                      <span className="text-[11px] text-gray-400 truncate">{locationText}</span>
                    )}
                  </div>
                </div>
              );
            })
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}

export default CustomerCombobox;
